import React from "react";
import { Heart, Compass, Users, ShieldCheck, Sparkles, Globe2 } from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { TEAM } from "../data/site";

const VALUES = [
  { icon: ShieldCheck, title: "Trauma-informed", body: "Safety, choice, trust and collaboration shape our practice." },
  { icon: Globe2, title: "Culturally responsive", body: "We take language, faith, family and history into account." },
  { icon: Users, title: "Community-led", body: "People with lived experience help design what we do." },
  { icon: Heart, title: "Dignity first", body: "Every person we meet is treated as the expert on their own life." },
];

export default function About() {
  useReveal();
  usePageMeta({ title: "About Us", description: "Our mission, values and the people behind Thrive Bridge Mental Wellness CIC." });

  return (
    <>
      <PageHero
        eyebrow="About us"
        title="About Thrive Bridge Mental Wellness CIC"
        lead="We are a UK Community Interest Company providing trauma-informed mental health and wellbeing support to adults facing social and economic disadvantage."
        image="https://images.unsplash.com/photo-1694286080661-f44117e019ed?crop=entropy&cs=srgb&fm=jpg&w=1200"
        alt="A group of people standing together"
      />

      {/* Mission / vision */}
      <section className="container-page section-spacing grid gap-10 lg:grid-cols-2">
        <article className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-8 md:p-10">
          <Compass className="h-8 w-8 text-[var(--tb-primary)] mb-4" />
          <h2 className="font-serif-display text-3xl text-[var(--tb-text)] mb-3">Our mission</h2>
          <p className="text-[var(--tb-text-2)] text-lg text-pretty">
            To provide trauma-informed, culturally responsive mental health and wellbeing support to adults
            experiencing migration, displacement, violence, abuse, homelessness, poverty and social exclusion —
            helping them rebuild confidence and access pathways to stability and independence.
          </p>
        </article>
        <article className="fade-up bg-[var(--tb-surface)] border border-[var(--tb-border)] rounded-3xl p-8 md:p-10">
          <Sparkles className="h-8 w-8 text-[var(--tb-primary)] mb-4" />
          <h2 className="font-serif-display text-3xl text-[var(--tb-text)] mb-3">Our vision</h2>
          <p className="text-[var(--tb-text-2)] text-lg text-pretty">
            A UK where mental wellbeing support is accessible to everyone, regardless of culture, language or
            life circumstance.
          </p>
        </article>
      </section>

      {/* Values */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Our values</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)]">
              The principles that guide our work
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <article key={v.title} className="fade-up bg-white border border-[var(--tb-border)] rounded-2xl p-6">
                <v.icon className="h-7 w-7 text-[var(--tb-primary)] mb-3" />
                <h3 className="font-serif-display text-xl mb-2 text-[var(--tb-text)]">{v.title}</h3>
                <p className="text-[var(--tb-text-2)]">{v.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Our story */}
      <section className="container-page section-spacing grid gap-10 lg:grid-cols-12 items-center">
        <div className="lg:col-span-5 fade-up">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-[var(--tb-border)]">
            <img
              src="https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?crop=entropy&cs=srgb&fm=jpg&w=900"
              alt="Hands stacked together demonstrating peer support"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-7 fade-up">
          <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Our story</span>
          <h2 className="font-serif-display mt-3 text-3xl md:text-4xl text-[var(--tb-text)] mb-4">
            Our story
          </h2>
          <p className="text-[var(--tb-text-2)] text-lg mb-4 text-pretty">
            Thrive Bridge was founded in response to a gap in provision: adults facing complex disadvantage were
            being turned away from, or falling through, mainstream mental health services. We began with
            conversations in community spaces and built the organisation from there.
          </p>
          <p className="text-[var(--tb-text-2)] text-lg text-pretty">
            We now deliver peer support, wellbeing workshops, one-to-one sessions and outreach, and work with
            local authorities, NHS services and grassroots groups across the UK.
          </p>
        </div>
      </section>

      {/* Team */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Meet the team</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)]">
              Our team
            </h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m) => (
              <article key={m.name} className="fade-up bg-white border border-[var(--tb-border)] rounded-2xl p-6">
                <div className="aspect-square rounded-2xl bg-[var(--tb-surface)] mb-4 grid place-items-center overflow-hidden">
                  <span className="font-serif-display text-5xl text-[var(--tb-primary)]">
                    {m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </span>
                </div>
                <h3 className="font-serif-display text-xl text-[var(--tb-text)]">{m.name}</h3>
                <p className="text-sm text-[var(--tb-primary)] font-medium">{m.role}</p>
                <p className="mt-2 text-sm text-[var(--tb-text-2)]">{m.bio}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CIC explainer */}
      <section className="container-page section-spacing">
        <div className="rounded-3xl bg-[var(--tb-primary)] text-white p-8 md:p-14">
          <span className="text-xs font-semibold tracking-widest uppercase text-white/80">CIC status</span>
          <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-balance">
            What being a Community Interest Company means
          </h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2 max-w-4xl text-white/95">
            <p className="text-lg text-pretty">
              A CIC is a UK company designed to use its profits and assets for the public good. Every penny we
              raise is reinvested in the community we serve.
            </p>
            <p className="text-lg text-pretty">
              An <em>asset lock</em> means our resources are legally required to remain dedicated to our mission.
              This provides transparency and accountability to the communities we work with.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
